import React, { useState } from "react";
import { Award, ArrowDownCircle, Eye, Loader2 } from "lucide-react";
import { promoteGold, demoteTable } from "../../lib/uploadsApi";
import { StageTrack, Pill, ScoreBars, scoreTone, SOURCE, btn, when, num } from "../pipeline/Layer";
import { cn } from "../../lib/utils";

const SEV = { blocking: "critical", major: "warning", minor: "info" };

function Sources({ t }) {
  const src = t.sources || [];
  return (
    <div data-testid={`ct-sources-${t.type}`}>
      <div className="kpi-label mb-1">Came from {src.length} Bronze item{src.length === 1 ? "" : "s"}</div>
      <div className="space-y-0.5 max-h-[110px] overflow-y-auto pr-1">{src.slice().reverse().map(s => (
        <div key={`${s.id}-${s.at}`} className="text-[11.5px] flex gap-2"><Pill tone="neutral">{SOURCE[s.source] || "Upload"}</Pill>
          <span className="truncate flex-1">{s.filename}{s.sheet ? ` · ${s.sheet}` : ""}</span>
          <span className="font-mono-tabular text-muted whitespace-nowrap">{num(s.rows)} rows · {s.mode} · {when(s.at)}</span></div>))}
        {!src.length && <div className="text-[11.5px] text-muted">—</div>}</div>
    </div>
  );
}

// One curated table (Silver or certified Gold): quality, findings, Bronze sources and the stage actions.
export default function CuratedCard({ t, canEdit, onFinding, onRows, onChanged, onToast }) {
  const q = t.quality || {};
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const run = async (k, fn, msg) => { setBusy(k); setErr(null); try { await fn(); onToast(msg); await onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(null); } };
  const gold = t.stage === "gold";
  return (
    <div className="tile p-4 space-y-3" data-testid={`ct-${t.type}`}>
      <div className="flex flex-wrap items-center gap-2"><div className="font-semibold text-sm flex-1">{t.label}</div><StageTrack stage={t.stage} testid={`ct-stage-${t.type}`} />
        {gold && <Pill tone={t.needs_review ? "warning" : "success"} testid={`ct-cert-${t.type}`}>{t.needs_review ? "Needs review" : "Certified"}</Pill>}</div>
      <div className="flex items-end gap-4">
        <div><div className="kpi-label">Quality</div><div className={cn("kpi-value text-3xl", scoreTone(q.score))} data-testid={`ct-score-${t.type}`}>{q.score ?? "—"}</div></div>
        <div className="flex-1"><ScoreBars dims={q.dims} /></div>
      </div>
      <div className="text-[11px] text-muted font-mono-tabular" data-testid={`ct-rows-${t.type}`}>{num(t.rows)} rows · updated {when(t.updated_at)} by {t.updated_by}{gold && t.certified_at ? ` · certified ${when(t.certified_at)} by ${t.certified_by}` : ""}</div>
      <div className="divide-y divide-border border border-border rounded-sm max-h-[150px] overflow-y-auto">
        {(q.findings || []).map(f => <button key={f.id} data-testid={`ct-finding-${t.type}-${f.id}`} onClick={() => onFinding(t.type, f.id)} className="w-full text-left px-3 py-1.5 text-[12px] flex gap-2 hover:bg-surface-2">
          <Pill tone={SEV[f.severity] || "info"}>{f.severity}</Pill><span className="flex-1">{f.title}</span></button>)}
        {!q.findings?.length && <div className="px-3 py-2 text-[12px] text-muted">No findings.</div>}
      </div>
      <Sources t={t} />
      {err && <div className="text-critical text-[12px]" data-testid={`ct-error-${t.type}`}>{err}</div>}
      <div className="flex flex-wrap gap-2">
        <button data-testid={`ct-open-${t.type}`} onClick={() => onRows(t.type)} className={cn(btn, "border border-border hover:border-primary")}><Eye size={13} />Open rows</button>
        {canEdit && (!gold || t.needs_review) && <button data-testid={`ct-gold-${t.type}`} disabled={!!busy} onClick={() => run("gold", () => promoteGold(t.type), `${t.label} certified Gold.`)} className={cn(btn, "bg-primary text-primary-fg hover:opacity-90")}>
          {busy === "gold" ? <Loader2 size={13} className="animate-spin" /> : <Award size={13} />}{gold ? "Re-certify Gold" : "Promote to Gold"}</button>}
        {canEdit && gold && <button data-testid={`ct-demote-${t.type}`} disabled={!!busy} onClick={() => run("demote", () => demoteTable(t.type), `${t.label} moved back to Silver.`)} className={cn(btn, "border border-border hover:border-warning")}>
          <ArrowDownCircle size={13} />Back to Silver</button>}
      </div>
    </div>
  );
}
